<script setup lang="ts">
import type { PropType } from 'vue';
import GaiaBreadcrumb from '../domain/navigation/GaiaBreadcrumb.vue';
import GaiaTopbar from '../domain/navigation/GaiaTopbar.vue';
import { gaiaBaseTextSize, gaiaContentWidth, gaiaShellPadding } from '../themes/tokens';

defineProps({
  /** Right drawer open state — a plain prop, the parent view owns the toggle. */
  drawerOpen: {
    type: Boolean,
    default: false,
  },
  /** Forces the drawer open, ignoring drawerOpen. */
  drawerPersistent: {
    type: Boolean,
    default: false,
  },
  drawerWidth: {
    type: [String, Number] as PropType<string | number>,
    default: 336,
  },
  /**
   * Cap for the main content column, centred in whatever space the drawer
   * leaves. Pass a step off `gaiaContentWidth` rather than a raw px value —
   * `table` for a list or a table-bodied detail page, `record` for a CM record,
   * `wide` for a workspace, `page` (the default) for forms and the home board,
   * `prose` for the reading measure.
   */
  contentMaxWidth: {
    type: String,
    default: gaiaContentWidth.page,
  },
  /**
   * Padding around the content column. The top comes from `gaiaShellPadding`,
   * shared with the drawer's GaiaPanel so both columns start at the same y; the
   * sides are `pageX`, the same edge the breadcrumb bar above uses. Keep the
   * default vertically symmetric — ChatView sizes itself to `100vh - 2 × top`.
   */
  contentPadding: {
    type: String,
    default: `${gaiaShellPadding.top} ${gaiaShellPadding.pageX}`,
  },
  /**
   * Gives the content box the body's full height as a flex column, so a page
   * that manages its own scrolling (a pinned toolbar over a grid, a split with
   * two panes) can put `flex: 1; min-height: 0` on its scroller. Off by
   * default: a normal page flows and the body scrolls it.
   */
  fill: {
    type: Boolean,
    default: false,
  },
  /**
   * `absolute` fills the parent and gives the content column and the drawer a
   * scroll container each, so they scroll independently. `static` grows with
   * its content and leaves scrolling to whatever mounts it — one shared
   * scrollbar for both columns.
   */
  position: {
    type: String as PropType<'static' | 'absolute'>,
    default: 'absolute',
  },
});

// Page-level body size. Components inside name a role from the type scale
// instead; anything that doesn't ask inherits this.
const baseTextSize = gaiaBaseTextSize;
</script>

<template>
  <!-- Structural mount point: the breadcrumb bar comes from the #breadcrumb
       slot (n-breadcrumb-item children — the bar and the breadcrumb are the
       layout's), the drawer chrome from the slotted content (e.g. GaiaPanel).
       The sider is a flex sibling of the column, so collapsing it pushes the
       bar instead of overlaying it. -->
  <n-layout
    has-sider
    sider-placement="right"
    :position="position"
    class="gaia-layout"
    :style="{ fontSize: baseTextSize }"
  >
    <!-- A plain column, not an inner n-layout: the body must be a flex child
         with a definite height for the bar to stay put while the body scrolls
         (same reason WizardLayout and WorkspaceLayout build theirs by hand). -->
    <div class="gaia-layout__column">
      <gaia-topbar v-if="$slots.breadcrumb" class="gaia-layout__topbar">
        <gaia-breadcrumb data-tour="layout.breadcrumb">
          <slot name="breadcrumb" />
        </gaia-breadcrumb>
      </gaia-topbar>
      <n-layout-content class="gaia-layout__body">
        <!-- The reading column: naive has no primitive that caps and centres a
             page's own content width, so the box is ours, driven by the props. -->
        <div
          class="gaia-layout__content"
          :class="{ 'gaia-layout__content--fill': fill }"
          :style="{ maxWidth: contentMaxWidth, margin: '0 auto', padding: contentPadding }"
        >
          <slot />
        </div>
      </n-layout-content>
    </div>

    <n-layout-sider
      v-if="$slots.drawer"
      collapse-mode="width"
      :collapsed-width="0"
      :width="drawerWidth"
      :collapsed="!drawerPersistent && !drawerOpen"
    >
      <slot name="drawer" />
    </n-layout-sider>
  </n-layout>
</template>

<style scoped>
.gaia-layout__column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.gaia-layout__topbar {
  flex: none;
}

/* Beats naive's own `.n-layout { flex: auto }` on specificity; `min-height: 0`
   is what lets the body shrink to the column and scroll instead of growing. */
.gaia-layout__body {
  flex: 1 1 auto;
  min-height: 0;
}

.gaia-layout__content--fill {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}
</style>
